<template>
  <div content>
    <div v-if="!loading">
      <vue-simplemde :preview-class="classObject" :configs="configs" v-model="editorData" ref="markdownEditor" />
    </div>
    <file-input :initial-modal="true" :profile="'image'" :disabledProfiles="['meta', 'header', 'avatar', 'partner', 'theme']" :control="showFileInput" @complete="insertImage" @canceled="hideInput"></file-input>
    <loader :loading="loading" :fixed="false"></loader>
  </div>
</template>

<script>
import VueSimplemde from 'vue-simplemde'
import loader from '@/components/Loader.vue'
import fileInput from '@/components/admin/FileInput.vue'
import { confirm } from '@/mixins/confirm.js'
import { getFilenameFromUrl } from '@/mixins/getFilenameFromUrl.js'

export default {
  name: 'markdown',
  mixins: [confirm, getFilenameFromUrl],
  data () {
    return {
      showFileInput: false,
      configs: {},
      oldImages: [],
      uploadedImages: [],
      customContainers: [
        {
          id: 'contact-form'
        }
      ],
      staticShowMenu: false,
      staticFullWidth: false,
      loading: false,
      customElements: [
        {
          name: 'Charts Bottom',
          value: 'content-charts-bottom',
          props: ['text', 'countryCode']
        }, {
          name: 'Charts Top',
          value: 'content-charts-top',
          props: ['text', 'countryCode']
        }, {
          name: 'Contact formulier',
          value: 'contactform',
          props: []
        }, {
          name: 'Downloads',
          value: 'downloads-list',
          props: []
        }, {
          name: 'Bibliotheek',
          value: 'library',
          props: []
        }, {
          name: 'Map',
          value: 'content-map',
          props: ['text', 'targets', 'countryCode', 'showMsmBanner', 'showABInBevBanner']
        }, {
          name: 'Partners',
          value: 'partners',
          props: ['ignore']
        }, {
          name: 'Werknemers',
          value: 'staff',
          props: []
        }, {
          name: 'Star App',
          value: 'star-app',
          props: ['slides']
        }, {
          name: 'Star Footer',
          value: 'star-footer',
          props: ['brochure', 'newsletter']
        }, {
          name: 'Star Partner',
          value: 'star-partner',
          props: ['id', 'text']
        }
      ]
    }
  },
  props: {
    value: String,
    notInToolbar: {
      type: Array,
      default: () => []
    },
    keepactive: {
      type: Boolean,
      default: false
    },
    autosave: {
      type: Boolean,
      default: true
    },
    wordCount: {
      type: Boolean,
      default: true
    }
  },
  components: {
    VueSimplemde,
    loader,
    fileInput
  },
  computed: {
    simplemde: {
      get () {
        return this.$refs.markdownEditor.simplemde
      },
      set (value) {
        this.$refs.markdownEditor.simplemde = value
      }
    },
    editorData: {
      get () {
        if (this.value !== '') {
          this.setOldImages(this.value)
        }
        return this.value
      },
      set (value) {
        this.handleUpdate(value)
      }
    },
    activePage () {
      return this.$store.state.activePage
    },
    classObject () {
      return 'content'
    }
  },
  beforeDestroy () {
    var self = this
    if (!self.keepactive) {
      self.closeEditor()
    }
  },
  beforeMount () {
    var self = this
    self.staticShowMenu = !!self.activePage.Cultures[self.$store.state.language].Content.ShowMenu
    var config = {
      status: [],
      spellChecker: false,
      previewRender: function (plainText, preview) {
        if (plainText.length > 0) {
          self.$http({
            method: 'POST',
            url: '/general/preview',
            name: process.env.VUE_APP_NAME + '_' + self.$store.state.language,
            api: 'website',
            data: JSON.stringify(plainText),
            headers: {
              'Content-Type': 'application/json'
            }
          }).then(function (response) {
            preview.innerHTML = response.data.replace(/\/files\//g, (self.$store.state.imgBase + 'files/'))
          }).catch(function (err) {
            self.$store.dispatch('setSnack', {
              message: err.message
            })
            preview.innerHTML = ''
          })
        } else {
          return ''
        }
        return self.$resx('Markdown.Loading') + '...'
      },
      shortcuts: {
        'undo': 'Cmd-Z',
        'redo': 'Cmd-Y'
      },
      toolbar: [
        'undo', 'redo', '|',
        {
          name: 'selectAll',
          action: function (editor) {
            self.simplemde.codemirror.focus()
            self.simplemde.codemirror.execCommand('selectAll')
          },
          className: 'fa fa-align-left',
          title: self.$resx('Markdown.SelectAll')
        },
        '|', 'bold', 'italic', 'heading-1', 'heading-2', 'heading-3', 'heading-smaller', 'heading-bigger', '|', 'unordered-list', 'ordered-list', '|', 'link',
        {
          name: 'image',
          action: function (editor) {
            self.showFileInput = true
          },
          className: 'fa fa-picture-o'
        },
        {
          name: 'embed',
          action: function (editor) {
            self.handleInsertEmbedClick(editor, this)
          },
          className: 'fa fa-code'
        },
        {
          name: 'insertSpacer',
          action: function (editor) { self.handleInsertSpacerClick(editor, this) },
          className: 'fa fa-expand',
          title: self.$resx('Markdown.insertSpacer')// 'Insert custom element'
        },
        'table', 'horizontal-rule', '|',
        {
          name: 'toggleMenu',
          action: function (editor) { self.handleShowMenuClick(editor, this) },
          className: 'fa fa-bars' + (self.staticShowMenu ? ' show' : ''),
          title: self.$resx('Markdown.ToggleSideMenu')// 'Toggle side menu'
        },
        {
          name: 'insertCustom',
          action: function (editor) { self.handleInsertCustomClick(editor, this) },
          className: 'fa fa-cubes',
          title: self.$resx('Markdown.InsertCustom')// 'Insert custom element'
        },
        {
          name: 'fullWidth',
          action: function (editor) { self.handleFullWidthClick(editor, this) },
          className: 'fa fa-arrows-alt' + (self.staticFullWidth ? ' show' : ''),
          title: 'Toggle full width'
        },
        '|', 'preview',
        {
          name: 'close',
          action: function (editor) {
            if (editor.value() !== self.value || self.activePage.Cultures[self.$store.state.language].Content.ShowMenu !== self.staticShowMenu) {
              self.confirm({
                message: self.$resx('Global.AreYouSure'),
                buttons: [
                  {
                    name: self.$resx('Buttons.Cancel'),
                    class: 'is-pulled-left'
                  },
                  {
                    name: self.$resx('Buttons.Yes'),
                    class: 'is-pulled-right is-danger',
                    action: function () {
                      self.activePage.Cultures[self.$store.state.language].Content.ShowMenu = self.staticShowMenu
                      if (!self.notInToolbar.includes('image')) {
                        for (var i = 0; i < self.uploadedImages.length; i++) {
                          self.deleteImage(self.uploadedImages[i])
                        }
                        self.uploadedImages = []
                      }
                      self.closeEditor()
                    }
                  }
                ]
              })
            } else {
              self.closeEditor()
            }
          },
          className: 'fa fa-times-circle pull-right',
          title: 'Close'
        },
        {
          name: 'save',
          action: function (editor) {
            self.saveContent(editor.value())
          },
          className: 'fa fa-save pull-right',
          title: self.$resx('Buttons.Save')
        }
      ]
    }
    if (self.autosave) {
      config.status.push('autosave')
      config.autosave = {
        enabled: true,
        uniqueId: 'autosave'
      }
    }
    if (self.wordCount) {
      config.status.push('words')
    }
    var toolbar = []
    for (var i = 0; i < config.toolbar.length; i++) {
      var item = config.toolbar[i].name || config.toolbar[i]
      if (!self.notInToolbar.includes(item)) {
        if (!(item === '|' && toolbar[(toolbar.length - 1)] === '|')) {
          toolbar.push(config.toolbar[i])
        }
      }
    }

    config.toolbar = toolbar
    self.configs = config
  },
  methods: {
    insertContainer: function (options) {
      var self = this
      var cm = self.simplemde.codemirror
      var text = '\n::: {#' + options.id + '}\n'
      text += ':::\n'
      cm.replaceSelection(text)
      cm.focus()
    },
    insertImage: function (data) {
      var self = this
      self.showFileInput = false
      var cm = self.simplemde.codemirror
      var options = self.simplemde.options
      var inline = data.profile === 'inline' || data.profile === 'icon'
      var cursor = cm.getCursor('from')
      var file = self.getFilenameFromUrl(data.url)
      if (file) {
        var imageUrl = (file.thumbUrl ? '/' + file.thumbUrl : '/' + file.fullUrl)

        self.setImage(imageUrl)
        // var urlsplit = file.thumbUrl.split('_')
        var dimensions = data.profile === 'icon' ? [data.thumbWidth, data.thumbWidth] : false
        var strStart = [options.insertTexts.image[0].slice(0, 2), data.alt, options.insertTexts.image[0].slice(2)].join('')
        var text = ''
        if (!inline) {
          text += '\n{.image'
          if (data.align) { text += ' .is-' + data.align }
          if (data.enlargeable) { text += ' .is-enlargeable' }
          text += '}\n'
          text += '^^^\n'
        }
        text += strStart + options.insertTexts.image[1].replace('#url#', imageUrl)
        if (dimensions) {
          if (inline) {
            text += '{style="width:auto; height:' + dimensions[1] + 'px"}'
          } else {
            text += '{style="max-width:' + dimensions[0] + 'px;max-height:' + dimensions[1] + 'px"}'
          }
        }
        if (!inline) {
          text += '\n^^^'
          if (data.caption && data.caption !== '') { text += ' ' + data.caption }
          text += '\n'
        }
        cm.replaceSelection(text)
        cm.focus()
        if (data.alt === '') { cm.setCursor({ line: (cursor.line + 3), ch: 2 }) }
      }
    },
    hideInput: function () {
      var self = this
      self.showFileInput = false
    },
    handleUpdate: function (value) {
      var self = this
      self.$emit('update', value)
    },
    handleShowMenuClick: function (editor, button) {
      var self = this
      var toolbarButton = editor.toolbarElements['toggleMenu']
      if (self.activePage.Cultures[self.$store.state.language].Content.ShowMenu) {
        toolbarButton.className = toolbarButton.className.replace(/\s*show\s*/g, '')
      } else {
        toolbarButton.className += ' show'
      }
      self.activePage.Cultures[self.$store.state.language].Content.ShowMenu = !self.activePage.Cultures[self.$store.state.language].Content.ShowMenu
    },
    handleInsertEmbedClick: function () {
      var self = this
      var value = ''

      var message = `
        <div class="field"><div class="control">
          <label class="label">${self.$resx('Markdown.Url')}</label>
          <input class="input" type="text" @change="emitEvent($event, 'changeText')" />
        </div></div>`

      self.confirm({
        isTemplate: true,
        message: message,
        buttons: [
          {
            name: self.$resx('Buttons.Cancel'),
            class: 'is-pulled-left'
          },
          {
            name: self.$resx('Buttons.Add'),
            class: 'is-pulled-right is-success',
            action: function () {
              if (value && value !== '') {
                var cm = self.simplemde.codemirror
                var html = '<figure class="image is-16by9">\n'
                html += '<iframe class="has-ratio" src="' + value + '" frameborder="0" allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe>\n'
                html += '</figure>'
                cm.replaceSelection(html)
                cm.focus()
              }
            }
          }
        ],
        events: {
          changeText: function (e) {
            value = e.target.value
          }
        }
      })
    },
    handleInsertCustomClick: function (editor, button) {
      var self = this
      var selected = false
      var options = ``
      for (var i = 0; i < self.customElements.length; i++) {
        options += `<option value="` + self.customElements[i].value + `">` + self.customElements[i].name + `</option>`
      }
      var message = `
        <div class="select">
          <select @change="emitEvent($event, 'changeSelect')"><option selected disabled>${self.$resx('Markdown.SelectElement')}</option>` +
            options +
          `</select>
        </div>`

      self.confirm({
        isTemplate: true,
        message: message,
        buttons: [
          {
            name: self.$resx('Buttons.Cancel'),
            class: 'is-pulled-left'
          },
          {
            name: self.$resx('Buttons.Choose'),
            class: 'is-pulled-right is-success',
            action: function () {
              if (selected && selected !== '') {
                var value = ''
                var props = ''
                self.customElements.forEach(element => {
                  if (element.value === selected) {
                    value = element.value
                    element.props.forEach((prop) => {
                      props += ' :' + prop + '=""'
                    })
                  }
                })
                var cm = self.simplemde.codemirror
                var text = '<' + value + props + '></' + value + '>'
                cm.replaceSelection(text)
                cm.focus()
              }
            }
          }
        ],
        events: {
          changeSelect: function (e) {
            selected = e.target.value
          }
        }
      })
    },
    handleInsertSpacerClick: function (editor, button) {
      var self = this
      var selected = false
      var sizes = ['xs', 'sm', 'md', 'lg']
      var options = ``
      for (var i = 0; i < sizes.length; i++) {
        options += `<option value="` + sizes[i] + `">` + sizes[i] + `</option>`
      }
      var message = `
        <div class="select">
          <select @change="emitEvent($event, 'changeSelect')"><option selected disabled>${self.$resx('Markdown.SelectElement')}</option>` +
            options +
          `</select>
        </div>`

      self.confirm({
        isTemplate: true,
        message: message,
        buttons: [
          {
            name: self.$resx('Buttons.Cancel'),
            class: 'is-pulled-left'
          },
          {
            name: self.$resx('Buttons.Choose'),
            class: 'is-pulled-right is-success',
            action: function () {
              if (selected && selected !== '') {
                var cm = self.simplemde.codemirror
                var text = '<space size="' + selected + '" />'
                cm.replaceSelection(text)
                cm.focus()
              }
            }
          }
        ],
        events: {
          changeSelect: function (e) {
            selected = e.target.value
          }
        }
      })
    },
    handleFullWidthClick: function (editor, button) {
      var self = this
      var toolbarButton = editor.toolbarElements['fullWidth']
      if (self.activePage.Cultures[self.$store.state.language].Content.IsFullwidth) {
        toolbarButton.className = toolbarButton.className.replace(/\s*show\s*/g, '')
      } else {
        toolbarButton.className += ' show'
      }
      self.activePage.Cultures[self.$store.state.language].Content.IsFullwidth = !self.activePage.Cultures[self.$store.state.language].Content.IsFullwidth
    },
    closeEditor: function () {
      var self = this
      if (self.autosave) { self.simplemde.clearAutosavedValue() }
      self.simplemde.toTextArea()
      self.simplemde = null
      self.$emit('close')
    },
    removeUnneededImages: function (o, n) {
      var self = this

      for (var i = 0; i < o.length; i++) {
        if (!n.includes(o[i])) {
          self.deleteImage(o[i])
        }
      }
      for (i = 0; i < self.uploadedImages.length; i++) {
        if (!n.includes(self.uploadedImages[i])) {
          self.deleteImage(self.uploadedImages[i])
        }
      }
      self.uploadedImages = []
    },
    deleteImage: function (url) {
      var self = this
      var file = self.getFilenameFromUrl(url)
      self.$store.dispatch('deleteFile', file)
    },
    setImage: function (url) {
      this.$emit('setImage', url)
      this.uploadedImages.push(url)
    },
    setOldImages: function (value) {
      this.oldImages = this.filterImages(value)
    },
    filterImages: function (input) {
      var rgx = /!\[.*?\]\((.*?)\)|url\((.*?)\)|img:'(.*?)'/g
      var match = rgx.exec(input)
      var tmp = []
      while (match != null) {
        if (match[1]) {
          tmp.push(match[1])
        } else if (match[2]) {
          tmp.push(match[2])
        } else if (match[3]) {
          tmp.push(match[3])
        }
        match = rgx.exec(input)
      }
      return tmp
    },
    saveContent: function (value) {
      var self = this
      if (value !== self.value || self.activePage.Cultures[self.$store.state.language].Content.ShowMenu !== self.staticShowMenu) {
        self.confirm({
          message: self.$resx('Global.AreYouSure'),
          buttons: [
            {
              name: self.$resx('Buttons.Cancel'),
              class: 'is-pulled-left'
            },
            {
              name: self.$resx('Buttons.Save'),
              class: 'is-pulled-right is-success',
              action: function () {
                self.confirmContent(value)
              }
            }
          ]
        })
      } else {
        self.confirmContent(value)
      }
    },
    confirmContent: function (value) {
      var self = this
      self.$emit('saveContent', value)
      if (!self.notInToolbar.includes('image')) {
        var images = self.filterImages(value)
        self.removeUnneededImages(self.oldImages, images)
      }
      self.closeEditor()
    }
  },
  watch: {
    value: {
      handler: function (val) {
        var self = this
        if (val !== '') {
          self.loading = false
        }
      },
      immediate: true
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~simplemde/dist/simplemde.min.css';

div[content]{
  position: relative;
  @include content;

  /deep/ .vue-simplemde {
    .editor-toolbar{
      a{
        &:focus{
          outline: none;
        }
        &.fa-bars, &.fa-arrows-alt{
          position: relative;
          &:after{
            content: "\f00c";
            font-size: 65%;
            vertical-align: text-bottom;
            position: absolute;
            top: 3px;
            right: 3px;
            padding-left: 1px;
            padding-bottom: 1px;
            width: 10px;
            background-color: $white;
            border-radius: 50%;
            display: inline-block;
            color: $success;
          }
          &:not(.show){
            &:after{
              content: "\f00d";
              color: $danger;
            }
          }
        }
        &.fa-align-left{
          position: relative;
          &:after{
            content: '';
            position: absolute;
            top: 4px;
            left: 3px;
            bottom: 4px;
            right: 3px;
            border-radius: 1px;
            border: 1px dashed;
          }
        }
      }
    }
  }
}
</style>
